import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cake as CakeIcon,
  Loyalty as LoyaltyIcon,
  Stars as StarsIcon,
  Schedule as ScheduleIcon,
  InfoOutlined as InfoIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import LoyaltyService from '../../services/loyalty.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { apiError } from './loyaltyUtils';

/**
 * One settings section: its own card with a brand-tinted icon tile, title + hint, then content.
 */
const SectionCard = ({ icon, title, hint, children }) => {
  const theme = useTheme();
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', overflow: 'visible' }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 }, '&:last-child': { pb: { xs: 2.5, sm: 3 } } }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" mb={children ? 2.5 : 0}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: theme.palette.primary.main,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)}, ${alpha(theme.palette.secondary.main, 0.16)})`,
            }}
          >
            {icon}
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>{title}</Typography>
            {hint && <Typography variant="body2" color="text.secondary" mt={0.25}>{hint}</Typography>}
          </Box>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
};

/** Big centered number input used for point values. */
const bigNumberSx = {
  width: { xs: '100%', sm: 220 },
  '& input': { fontSize: 28, fontWeight: 800, py: 1.5, letterSpacing: '-0.5px' },
  '& .MuiInputAdornment-root p': { fontWeight: 600 },
};

const LoyaltySettings = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, hasPermission, refreshUser } = useAuth();
  const canManage = hasPermission('loyalty.manage');
  const isLoyaltyProfile = user?.tenant?.profile === 'loyalty';
  const [form, setForm] = useState({ enabled: false, points_per_currency: 1, birthday_bonus_points: 0 });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    LoyaltyService.getSettings()
      .then((r) => setForm({
        enabled: !!r.data?.data?.enabled,
        points_per_currency: r.data?.data?.points_per_currency ?? 1,
        birthday_bonus_points: r.data?.data?.birthday_bonus_points ?? 0,
      }))
      .catch((error) => setSnackbar({ open: true, message: apiError(error, t('loyalty.errorLoading')), severity: 'error' }));
  }, [t]);

  const save = async () => {
    try {
      setSaving(true);
      await LoyaltyService.updateSettings({
        enabled: form.enabled,
        points_per_currency: parseFloat(form.points_per_currency),
        birthday_bonus_points: parseInt(form.birthday_bonus_points, 10) || 0,
      });
      setSnackbar({ open: true, message: t('loyalty.settingsSaved'), severity: 'success' });
      if (typeof refreshUser === 'function') refreshUser();
    } catch (error) {
      setSnackbar({ open: true, message: apiError(error, t('loyalty.errorSaving')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const ppc = parseFloat(form.points_per_currency) || 0;
  const bonus = parseInt(form.birthday_bonus_points, 10) || 0;
  const gradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`;

  const active = form.enabled || isLoyaltyProfile;

  return (
    <Box sx={{ maxWidth: 1280 }}>
      {/* Page header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800} letterSpacing="-0.5px">{t('loyalty.settings')}</Typography>
          <Typography variant="body1" color="text.secondary" mt={0.5}>{t('loyalty.settingsSubtitle')}</Typography>
        </Box>
        <Chip
          icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: active ? 'success.main' : 'text.disabled', ml: '10px !important' }} />}
          label={active ? t('loyalty.programActive') : t('loyalty.programInactive')}
          variant="outlined"
          sx={{ fontWeight: 600, bgcolor: 'background.paper', alignSelf: { xs: 'flex-start', sm: 'auto' } }}
        />
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={2.5}>
            {/* Program */}
            <SectionCard icon={<LoyaltyIcon />} title={t('loyalty.programSettings')} hint={t('loyalty.enableHint')}>
              <Box
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: active ? alpha(theme.palette.primary.main, 0.4) : 'divider',
                  bgcolor: active ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                  px: 2,
                  py: 1,
                  transition: 'all .2s',
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                  labelPlacement="start"
                  control={<Switch checked={form.enabled || isLoyaltyProfile} disabled={!canManage || isLoyaltyProfile} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />}
                  label={<Typography fontWeight={600}>{t('loyalty.enableProgram')}</Typography>}
                />
              </Box>
              {isLoyaltyProfile && (
                <Stack direction="row" spacing={1} alignItems="center" mt={1.5} color="text.secondary">
                  <InfoIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">{t('loyalty.enabledByProfile')}</Typography>
                </Stack>
              )}
            </SectionCard>

            {/* Earning */}
            <SectionCard icon={<StarsIcon />} title={t('loyalty.earningRules')} hint={t('loyalty.pointsPerCurrencyHint')}>
              <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
                <Box
                  sx={{
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.75,
                    borderRadius: 2.5,
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                    textAlign: 'center',
                    minWidth: { xs: 64, sm: 96 },
                    flexShrink: 0,
                  }}
                >
                  <Typography fontSize={28} fontWeight={800} lineHeight={1.2}>Q1</Typography>
                </Box>
                <ArrowIcon color="action" />
                <TextField
                  type="number"
                  label={t('loyalty.pointsPerCurrency')}
                  value={form.points_per_currency}
                  disabled={!canManage}
                  onChange={(e) => setForm({ ...form, points_per_currency: e.target.value })}
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={{ ...bigNumberSx, width: { xs: 'auto', sm: 220 }, flex: { xs: 1, sm: 'none' } }}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">pts</InputAdornment> } }}
                />
              </Stack>
              <Box sx={{ mt: 2, px: 1.5, py: 1, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.08), display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize={16} lineHeight={1}>🛍️</Typography>
                <Typography variant="body2" fontWeight={600} color="success.dark">
                  {t('loyalty.previewPurchase').replace('{points}', Math.round(100 * ppc).toLocaleString())}
                </Typography>
              </Box>
            </SectionCard>

            {/* Birthday */}
            <SectionCard icon={<CakeIcon />} title={t('loyalty.birthdayBonus')} hint={t('loyalty.birthdayBonusHint')}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField
                  type="number"
                  label={t('loyalty.birthdayBonus')}
                  value={form.birthday_bonus_points}
                  disabled={!canManage}
                  onChange={(e) => setForm({ ...form, birthday_bonus_points: e.target.value })}
                  inputProps={{ min: 0, step: 1 }}
                  sx={bigNumberSx}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">pts</InputAdornment> } }}
                />
                <Chip
                  color={bonus > 0 ? 'primary' : 'default'}
                  variant={bonus > 0 ? 'filled' : 'outlined'}
                  label={bonus > 0 ? t('loyalty.birthdayBonusOn').replace('{points}', bonus.toLocaleString()) : t('loyalty.birthdayBonusOff')}
                  sx={{ fontWeight: 600, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" mt={2} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.07), color: 'text.secondary' }}>
                <ScheduleIcon sx={{ fontSize: 18, color: 'info.main' }} />
                <Typography variant="caption">{t('loyalty.birthdayBonusSchedule')}</Typography>
              </Stack>
            </SectionCard>

            {canManage && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={save}
                  disabled={saving}
                  sx={{ px: 4, borderRadius: 2.5, width: { xs: '100%', sm: 'auto' }, boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}` }}
                >
                  {t('common.save')}
                </Button>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* Live preview: what a member sees in the app with these settings */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box sx={{ position: { md: 'sticky' }, top: 88 }}>
            <Typography variant="overline" color="text.secondary" fontWeight={700} display="block" mb={1} textAlign="center">
              {t('loyalty.previewTitle')}
            </Typography>
            {/* Phone frame */}
            <Box
              sx={{
                mx: 'auto',
                maxWidth: 340,
                p: 1.25,
                borderRadius: '40px',
                bgcolor: '#111',
                boxShadow: '0 24px 48px rgba(15, 23, 42, 0.25)',
              }}
            >
              <Box sx={{ borderRadius: '32px', overflow: 'hidden', bgcolor: '#0b0b0f', color: '#fff', pb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                  <Box sx={{ width: 90, height: 22, borderRadius: 11, bgcolor: '#000' }} />
                </Box>
                {/* Membership card */}
                <Box sx={{ m: 1.75, mt: 1.5, p: 2.25, borderRadius: 4, background: gradient, position: 'relative', overflow: 'hidden' }}>
                  <Box sx={{ position: 'absolute', width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.10)', top: -60, right: -50 }} />
                  <Stack direction="row" spacing={1.25} alignItems="center" position="relative">
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.22)', fontWeight: 700, fontSize: 16 }}>
                      {(user?.tenant?.name || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography fontWeight={700} noWrap>{user?.tenant?.name}</Typography>
                  </Stack>
                  <Typography fontSize={36} fontWeight={800} lineHeight={1.1} mt={2} position="relative">
                    {Math.round(100 * ppc).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }} position="relative">pts</Typography>
                </Box>
                <Stack spacing={1} px={1.75}>
                  <PreviewRow
                    emoji="🛍️"
                    title={t('loyalty.previewPurchase').replace('{points}', Math.round(100 * ppc).toLocaleString())}
                    subtitle={t('loyalty.previewPurchaseSub')}
                  />
                  <PreviewRow
                    emoji="🎂"
                    title={bonus > 0 ? t('loyalty.previewBirthday').replace('{points}', bonus.toLocaleString()) : t('loyalty.previewBirthdayOff')}
                    subtitle={bonus > 0 ? t('loyalty.previewBirthdaySub') : t('loyalty.previewBirthdayOffSub')}
                    muted={bonus <= 0}
                  />
                  <PreviewRow emoji="🎁" title={t('loyalty.previewRewards')} subtitle={t('loyalty.previewRewardsSub')} />
                </Stack>
                {!form.enabled && !isLoyaltyProfile && (
                  <Alert severity="warning" variant="filled" sx={{ mx: 1.75, mt: 1.5, borderRadius: 2.5 }}>{t('loyalty.previewDisabled')}</Alert>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

const PreviewRow = ({ emoji, title, subtitle, muted }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{ bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, p: 1.5, opacity: muted ? 0.55 : 1 }}
  >
    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Typography fontSize={22} lineHeight={1}>{emoji}</Typography>
    </Box>
    <Box minWidth={0}>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>{subtitle}</Typography>
    </Box>
  </Stack>
);

export default LoyaltySettings;
